// Integração Ploomes — sync API "Zerar Ploomes" (2026-05-29)
// Lucas pediu: importar perdidos + propostas que ficaram lá não migradas.
// Token configurado pela UI (gravado no Secret dre/ploomes-token).
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Typography, Space, Tag, Alert, Divider,
  App as AntApp, Row, Col, Statistic, Descriptions, Modal,
} from 'antd';
import {
  KeyOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ExperimentOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

const { Title, Text, Paragraph } = Typography;

interface PloomesCfg {
  configurado: boolean;
  token_mascarado: string;
  base_url: string;
  ultima_sync: string | null;
  ultima_sync_status: string | null;
  ultima_sync_metricas: {
    paginas_lidas?: number;
    deals_lidos?: number;
    propostas_criadas?: number;
    propostas_atualizadas?: number;
    deals_pulados_sem_cliente?: number;
    deals_duplicados_pulados?: number;
    orders_orfas_importadas?: number;
    orders_orfas_sem_cliente?: number;
    valores_ajustados_via_order?: number;
    deals_ganhos_sem_order_pulados?: number;
    deals_promovidos_via_order?: number;
    deals_multi_order?: number;
    vendas_multi_order_criadas?: number;
    vendas_multi_order_migradas?: number;
    quotes_lidas?: number;
    quotes_baixados?: number;
    orders_baixados?: number;
    pdfs_pulados_ja_baixados?: number;
    pdfs_erros?: number;
    clientes_criados?: number;
    contatos_criados?: number;
    contatos_lidos?: number;
    erros?: any[];
    tempos?: { deals_ms?: number; contacts_ms?: number; total_ms?: number };
  } | null;
}

export default function IntegracaoPloomes() {
  const { message, modal } = AntApp.useApp();
  const [cfg, setCfg] = useState<PloomesCfg | null>(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [novoToken, setNovoToken] = useState('');
  const [tokenVisivel, setTokenVisivel] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const auth = await getToken();
      const r = await axios.get(`${API_URL}/comercial/ploomes`,
        { headers: { Authorization: auth } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCfg(data);
    } catch (e: any) {
      message.error('Erro ao carregar config: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message]);

  useEffect(() => { carregar(); }, [carregar]);

  // Polling: enquanto sync em andamento, recarrega a cada 8s
  useEffect(() => {
    if (cfg?.ultima_sync_status !== 'em_andamento') return;
    const t = setInterval(() => { carregar(); }, 8000);
    return () => clearInterval(t);
  }, [cfg?.ultima_sync_status, carregar]);

  async function salvarToken() {
    if (!novoToken.trim()) {
      message.warning('Cole o token Ploomes no campo.');
      return;
    }
    setSalvando(true);
    try {
      const auth = await getToken();
      await axios.post(`${API_URL}/comercial/ploomes`,
        { action: 'salvar_token', token: novoToken.trim() },
        { headers: { Authorization: auth } });
      message.success('Token salvo.');
      setNovoToken('');
      setTokenVisivel(false);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function testarToken() {
    setTestando(true);
    try {
      const auth = await getToken();
      const r = await axios.post(`${API_URL}/comercial/ploomes`,
        { action: 'testar_token' },
        { headers: { Authorization: auth }, timeout: 20000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`OK! Conectado como: ${d.usuario || d.email || '?'}`);
    } catch (e: any) {
      message.error('Falhou: ' + (e.response?.data?.error || e.message));
    } finally { setTestando(false); }
  }

  function dispararSync() {
    modal.confirm({
      title: 'Sincronizar Ploomes agora?',
      width: 540,
      content: (
        <div style={{ fontSize: 13 }}>
          Vou buscar <strong>todos os Deals</strong> e <strong>Contacts</strong> do Ploomes
          via API e mesclar com o sistema:
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>Negócios já importados antes (match por título + cliente) → atualizo status + motivo de perda</li>
            <li>Negócios novos (especialmente <strong>perdidos</strong> não trazidos) → crio em rascunho</li>
            <li>Clientes que não existem → crio com segmento "Importado Ploomes 2026"</li>
            <li>Contatos vinculados → crio</li>
          </ul>
          <Alert
            type="info" showIcon style={{ marginTop: 10 }}
            message="Dedup forte"
            description="Cada negócio do Ploomes recebe um ploomes_deal_id no banco. Rodadas seguintes desta sync ficam idempotentes — não duplicam nada."
          />
          <Paragraph style={{ marginTop: 10, fontSize: 11, color: '#888' }}>
            <strong>Tempo:</strong> 1-3 minutos dependendo do volume. Roda em segundo plano —
            esta tela mostra o resultado quando concluir.
          </Paragraph>
        </div>
      ),
      okText: 'Iniciar sync', cancelText: 'Cancelar',
      onOk: async () => {
        setSincronizando(true);
        try {
          const auth = await getToken();
          const r = await axios.post(`${API_URL}/comercial/ploomes`,
            { action: 'sync' },
            { headers: { Authorization: auth }, timeout: 25000 });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          message.success(d.mensagem || 'Sync iniciado.');
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        } finally { setSincronizando(false); }
      },
    });
  }

  function fmtMs(ms?: number) {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  }
  function fmtDataHora(iso: string | null) {
    if (!iso) return 'nunca';
    return new Date(iso).toLocaleString('pt-BR');
  }

  const statusBadge = () => {
    if (!cfg) return null;
    const st = cfg.ultima_sync_status;
    if (st === 'em_andamento') {
      return <Tag icon={<SyncOutlined spin />} color="processing">Em andamento</Tag>;
    }
    if (st === 'concluida') return <Tag color="success" icon={<CheckCircleOutlined />}>Concluída</Tag>;
    if (st === 'erro' || st === 'erro_invoke') {
      return <Tag color="error" icon={<CloseCircleOutlined />}>Erro</Tag>;
    }
    return <Tag>Nunca rodou</Tag>;
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <Title level={5} style={{ color: '#020D7F' }}>🔌 Ploomes (sincronização de pipeline)</Title>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        Importa negócios e contatos do Ploomes via API. Use pra <strong>"zerar o Ploomes"</strong>
        — trazer todos os perdidos + propostas que ficaram lá.
      </Paragraph>

      {/* ─── Token + conexão ─── */}
      <Card size="small" title={<Space><KeyOutlined /> Token de acesso</Space>}
        style={{ marginBottom: 14 }}>
        {loading ? <Text>Carregando…</Text> : cfg?.configurado ? (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space>
              <Text>Token configurado:</Text>
              <Text code>{cfg.token_mascarado}</Text>
              <Button size="small" icon={<ExperimentOutlined />} loading={testando}
                onClick={testarToken}>
                Testar conexão
              </Button>
            </Space>
            <Button size="small" type="link" onClick={() => setTokenVisivel(!tokenVisivel)}
              style={{ paddingLeft: 0 }}>
              {tokenVisivel ? 'Cancelar' : 'Trocar token'}
            </Button>
          </Space>
        ) : (
          <Alert type="warning" showIcon
            message="Token não configurado"
            description="Cole abaixo o token Ploomes (User-Key da Administração > Usuários de integração)." />
        )}

        {(tokenVisivel || !cfg?.configurado) && !loading && (
          <Space.Compact style={{ width: '100%', marginTop: 10 }}>
            <Input.Password value={novoToken} onChange={e => setNovoToken(e.target.value)}
              placeholder="Cole aqui o token Ploomes (User-Key)" />
            <Button type="primary" loading={salvando} onClick={salvarToken}>
              Salvar
            </Button>
          </Space.Compact>
        )}
      </Card>

      {/* ─── Sync ─── */}
      <Card size="small" title={<Space><SyncOutlined /> Sincronizar agora</Space>}
        style={{ marginBottom: 14 }}>
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Flex align="center" gap={12}>
            <Button type="primary" size="large" icon={<SyncOutlined />}
              loading={sincronizando || cfg?.ultima_sync_status === 'em_andamento'}
              disabled={!cfg?.configurado || cfg?.ultima_sync_status === 'em_andamento'}
              onClick={dispararSync}
              style={{ background: '#0930D0', borderColor: '#0930D0' }}>
              Sincronizar Ploomes
            </Button>
            <Space>
              <Text strong>Status:</Text>
              {statusBadge()}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined /> Última sync: {fmtDataHora(cfg?.ultima_sync || null)}
            </Text>
          </Flex>

          {/* ─── Métricas ─── */}
          {cfg?.ultima_sync_metricas && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Row gutter={12}>
                <Col span={6}>
                  <Statistic title="Deals lidos" value={cfg.ultima_sync_metricas.deals_lidos || 0}
                    valueStyle={{ fontSize: 18, color: '#0930D0' }} />
                </Col>
                <Col span={6}>
                  <Statistic title="Propostas criadas" value={cfg.ultima_sync_metricas.propostas_criadas || 0}
                    valueStyle={{ fontSize: 18, color: '#0a7f4a' }} />
                </Col>
                <Col span={6}>
                  <Statistic title="Propostas atualizadas" value={cfg.ultima_sync_metricas.propostas_atualizadas || 0}
                    valueStyle={{ fontSize: 18, color: '#B07500' }} />
                </Col>
                <Col span={6}>
                  <Statistic title="Tempo total" value={fmtMs(cfg.ultima_sync_metricas.tempos?.total_ms)}
                    valueStyle={{ fontSize: 18 }} />
                </Col>
              </Row>
              <Descriptions size="small" column={3} style={{ marginTop: 10 }}>
                <Descriptions.Item label="Clientes criados">
                  {cfg.ultima_sync_metricas.clientes_criados || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Contatos criados">
                  {cfg.ultima_sync_metricas.contatos_criados || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Erros">
                  {cfg.ultima_sync_metricas.erros?.length || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Pulados sem cliente"
                  contentStyle={{ color: '#888' }}>
                  {cfg.ultima_sync_metricas.deals_pulados_sem_cliente || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Duplicados pulados"
                  contentStyle={{ color: '#888' }}>
                  {cfg.ultima_sync_metricas.deals_duplicados_pulados || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Vendas órfãs importadas"
                  contentStyle={{ color: '#0930D0' }}
                  span={2}>
                  {cfg.ultima_sync_metricas.orders_orfas_importadas || 0}
                  {' '}<span style={{ color: '#888', fontSize: 11 }}>(Orders sem proposta no Ploomes)</span>
                </Descriptions.Item>
                <Descriptions.Item label="Valores ajustados via Order"
                  contentStyle={{ color: '#B07500' }}
                  span={2}>
                  {cfg.ultima_sync_metricas.valores_ajustados_via_order || 0}
                  {' '}<span style={{ color: '#888', fontSize: 11 }}>(Order.Amount diferente da proposta)</span>
                </Descriptions.Item>
                <Descriptions.Item label='Ganhos sem Order (não importa)'
                  contentStyle={{ color: '#888' }}
                  span={3}>
                  {cfg.ultima_sync_metricas.deals_ganhos_sem_order_pulados || 0}
                  {' '}<span style={{ color: '#888', fontSize: 11 }}>(Deal marcado ganho no Ploomes mas sem documento de venda — não conta como venda real)</span>
                </Descriptions.Item>
                <Descriptions.Item label='Promovidos via Order'
                  contentStyle={{ color: '#0930D0' }}
                  span={3}>
                  {cfg.ultima_sync_metricas.deals_promovidos_via_order || 0}
                  {' '}<span style={{ color: '#888', fontSize: 11 }}>(Deal "aberto" no Ploomes mas com Order emitida — promovido pra ganho. Ex: Paviplan)</span>
                </Descriptions.Item>
                <Descriptions.Item label='Deals multi-venda'
                  contentStyle={{ color: '#0930D0' }}
                  span={3}>
                  {cfg.ultima_sync_metricas.deals_multi_order || 0}
                  {' '}<span style={{ color: '#888', fontSize: 11 }}>
                    (Deal com N≥2 Orders — cada venda vira proposta separada. Ex: Ceriluz mar/2026 3×R$ 9k).
                    {' '}Criadas: {cfg.ultima_sync_metricas.vendas_multi_order_criadas || 0}
                    {' · '}Migradas (1ª venda de legado): {cfg.ultima_sync_metricas.vendas_multi_order_migradas || 0}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label='PDFs migrados'
                  contentStyle={{ color: '#0a7f4a' }}
                  span={3}>
                  Propostas (Quotes): {cfg.ultima_sync_metricas.quotes_baixados || 0}
                  {' · '}Vendas (Orders): {cfg.ultima_sync_metricas.orders_baixados || 0}
                  {' · '}<span style={{ color: '#888' }}>Já baixados (pulados): {cfg.ultima_sync_metricas.pdfs_pulados_ja_baixados || 0}</span>
                  {(cfg.ultima_sync_metricas.pdfs_erros || 0) > 0 && (
                    <>{' · '}<span style={{ color: '#B91626' }}>Erros: {cfg.ultima_sync_metricas.pdfs_erros}</span></>
                  )}
                  {' '}<span style={{ color: '#888', fontSize: 11, display: 'block', marginTop: 2 }}>
                    PDFs salvos em S3 (ploomes-docs/). Disponíveis nos botões 📄 da página da proposta.
                  </span>
                </Descriptions.Item>
              </Descriptions>
              {cfg.ultima_sync_metricas.erros && cfg.ultima_sync_metricas.erros.length > 0 && (
                <Alert type="warning" showIcon
                  message={`${cfg.ultima_sync_metricas.erros.length} erro(s) na sync — primeiros 3:`}
                  description={
                    <ul style={{ fontSize: 11, marginBottom: 0, paddingLeft: 18 }}>
                      {cfg.ultima_sync_metricas.erros.slice(0, 3).map((e, i) => (
                        <li key={i}>
                          <Text code>{e.etapa}</Text> · {e.erro || e.body || JSON.stringify(e).slice(0, 100)}
                        </li>
                      ))}
                    </ul>
                  }
                />
              )}
            </>
          )}
        </Space>
      </Card>

      {/* ─── Help ─── */}
      <Card size="small" type="inner" style={{ background: '#FAFBFF' }}>
        <Paragraph style={{ fontSize: 11, marginBottom: 6 }}>
          <strong>Como obter o token Ploomes?</strong>
        </Paragraph>
        <ol style={{ fontSize: 11, paddingLeft: 20, marginBottom: 0 }}>
          <li>Acessa o Ploomes como admin</li>
          <li>Administração → Integrações → Usuários de integração</li>
          <li>Cria um usuário "Hydrodata DRE-System" (ou usa existente)</li>
          <li>Copia a User-Key dele e cola aqui</li>
        </ol>
      </Card>
    </div>
  );
}

// Mantine-style Flex usando Space wrapper inline pra evitar import duplicado.
function Flex({ children, align, gap }: { children: React.ReactNode; align?: string; gap?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: (align as any) || 'center', gap: gap || 8, flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}
